import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import { getVideo } from '@/lib/api';
import { VideoThumb } from './Videos';

const fmt = (s) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

export default function VideoDetail() {
    const { slug } = useParams();
    const reveal = useReveal();
    const [data, setData] = useState(null);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        setPlaying(false);
        getVideo(slug).then(setData);
    }, [slug]);

    useDocumentMeta({
        title: data ? `${data.video.title} · Film · Maison Touré` : 'Film · Maison Touré',
        description: data ? data.video.description : undefined,
    });

    if (!data) {
        return (
            <div style={{ paddingTop: 200 }} className="max-w-[1600px] mx-auto px-8 md:px-16">
                <p className="serif-italic" style={{ fontSize: 22, color: 'var(--ink-2)' }}>Loading…</p>
            </div>
        );
    }

    const { video, related } = data;
    const embedSrc = `https://www.youtube-nocookie.com/embed/${video.youtube_id}?rel=0&modestbranding=1&color=white&iv_load_policy=3&autoplay=1`;

    /* schema.org VideoObject JSON-LD for SEO */
    const ld = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": video.title,
        "description": video.description,
        "thumbnailUrl": video.thumbnail,
        "uploadDate": video.published_at,
        "embedUrl": `https://www.youtube-nocookie.com/embed/${video.youtube_id}`,
        "contentUrl": video.youtube_url,
    };

    return (
        <article ref={reveal} data-testid="video-detail-page" style={{ paddingTop: 144 }}>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />

            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pt-8">
                <Link to="/film" className="overline" style={{ color: 'var(--ink-3)' }}>The Film Archive</Link>
            </section>

            {/* PLAYER */}
            <section className="max-w-[1400px] mx-auto px-8 md:px-16 mt-12 reveal">
                <div
                    className="img-wrap relative"
                    style={{ background: 'var(--ink)', aspectRatio: '16 / 9' }}
                    data-testid="video-player"
                >
                    {playing ? (
                        <iframe
                            title={video.title}
                            src={embedSrc}
                            style={{ width: '100%', height: '100%', border: 0 }}
                            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            data-testid="video-iframe"
                        />
                    ) : (
                        <button
                            type="button"
                            onClick={() => setPlaying(true)}
                            data-testid="video-play-btn"
                            className="absolute inset-0 w-full h-full p-0"
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                            aria-label={`Play ${video.title}`}
                        >
                            <VideoThumb video={video} />
                        </button>
                    )}
                </div>
            </section>

            {/* TITLE + CATALOGUE BLOCK */}
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
                <div className="md:col-span-8 reveal">
                    <span className="overline" style={{ color: 'var(--ink-2)' }}>{video.category}</span>
                    <h1
                        className="mt-6"
                        style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            fontWeight: 400,
                            fontSize: 'clamp(34px, 4.2vw, 70px)',
                            lineHeight: 1.02,
                            letterSpacing: '-0.022em',
                        }}
                    >
                        <em className="serif-italic" style={{ fontWeight: 500 }}>{video.title}</em>
                    </h1>
                    {video.description && (
                        <p
                            className="mt-10"
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 19,
                                lineHeight: 1.75,
                                color: 'var(--ink-2)',
                                maxWidth: 720,
                            }}
                        >
                            {video.description}
                        </p>
                    )}
                </div>
                <aside className="md:col-span-3 md:col-start-10 reveal">
                    <dl className="flex flex-col">
                        <Row label="Category" value={video.category} />
                        <Row label="Date" value={fmt(video.published_at)} />
                        {video.artist_name && (
                            <Row label="Artist" value={<Link to={`/artists/${video.artist_slug}`} className="hover:underline">{video.artist_name}</Link>} />
                        )}
                        {video.exhibition_title && (
                            <Row label="Exhibition" value={<Link to={`/exhibitions/${video.exhibition_slug}`} className="hover:underline">{video.exhibition_title}</Link>} />
                        )}
                    </dl>
                </aside>
            </section>

            {/* RELATED */}
            {related?.length > 0 && (
                <section className="rule-top" data-testid="video-related">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                        <header className="reveal mb-16">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>Further Films</span>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-16">
                            {related.map((v) => (
                                <Link key={v.slug} to={`/film/${v.slug}`} className="reveal group block">
                                    <VideoThumb video={v} />
                                    <div className="mt-7">
                                        <span className="overline" style={{ color: 'var(--ink-2)' }}>{v.category}</span>
                                        <h3
                                            className="mt-4"
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontWeight: 500,
                                                fontSize: 'clamp(22px, 2vw, 30px)',
                                                lineHeight: 1.16,
                                                letterSpacing: '-0.012em',
                                            }}
                                        >
                                            {v.title}
                                        </h3>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </article>
    );
}

function Row({ label, value }) {
    return (
        <div className="grid grid-cols-3 gap-4 py-4 rule-bottom">
            <dt className="overline" style={{ color: 'var(--ink-3)' }}>{label}</dt>
            <dd
                className="col-span-2"
                style={{
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                    fontWeight: 400,
                    fontSize: 16,
                    lineHeight: 1.45,
                    color: 'var(--ink)',
                }}
            >
                {value}
            </dd>
        </div>
    );
}
